import { createEffect, createMemo, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import {
  ariaSort,
  type FleetViewState,
  LIST_COLUMNS,
  type ListColumn,
  type PagedGroup,
  shortId,
} from '../lib/fleet-machines';
import type { UtilBox } from '../lib/fleetUtilization';
import type { ResourceKind } from '../lib/machine-bars';
import { MACHINE_HEALTH, MACHINE_STATUS, serverText } from '../lib/machine-labels';
import type { MachineHandlers } from '../lib/machine-panel';
import { absolute, ago } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { MachineGroups } from './MachineGroups';
import { MachineResourceBar } from './MachineResourceBar';
import { MachineTaskCell } from './MachineTaskCell';
import { MissionCardMenu } from './machine-card/MissionCardMenu';

// The Machines list's List view (#6561, prototype `row`): one page of machines as a table,
// one `DataTable` per server group under the group's header (executor note 2), every column
// sortable from its header. Columns: Machine (name + `<8-char id> · <server>`), Status,
// Doing now (task + repo), CPU, Memory, Disk, Health, Last seen, and the row menu.
// Presenter: `FleetMachines` hands it the page (`fleet-pager.ts`) and the sort, and every
// header press goes back up as a column (`sortPress` decides the order).
//
// KEYED BY ID, as the Cards view is: each table's rows are machine ids and every cell looks
// its machine up in the current read, so the 20-second poll updates a row in place instead
// of rebuilding it under an open menu or tooltip.
//
// THE NAME OPENS THE MACHINE'S DETAILS PANEL (#6563) for every viewer and every machine
// (owner ruling 2026-10-22); the panel is read-only for a member, a viewer or a machine
// from another org. THE ROW MENU IS THE CARD'S (#6785, `MissionCardMenu`) without
// "Simplify card": every viewer gets its read items, and its writes are an admin's on this
// org's machines (owner rulings G21, G23). The working dot is the one live surface,
// the design system's `.status__dot`, as on a card. Healthy is neutral, never green.

const HEADER: Readonly<Record<ListColumn, string>> = {
  name: 'fleet.machines.col_machine',
  status: 'fleet.machines.col_status',
  task: 'fleet.machines.col_task',
  cpu: 'fleet.machines.bar_cpu',
  memory: 'fleet.machines.bar_memory',
  disk: 'fleet.machines.bar_disk',
  health: 'fleet.machines.col_health',
  seen: 'fleet.machines.col_seen',
};

export type MachineTableProps = {
  readonly paged: readonly PagedGroup[];
  readonly byId: ReadonlyMap<string, UtilBox>;
  readonly view: FleetViewState;
  readonly onSort: (column: ListColumn) => void;
  readonly canWrite: boolean;
  readonly onManage: MachineHandlers['onManage'];
  readonly onRename: (box: UtilBox) => void;
  readonly onDrain: (box: UtilBox) => void;
  readonly onUndrain: (box: UtilBox) => void;
  readonly onRemove: (box: UtilBox) => void;
  /** The four the group header's server-name write needs — see `MachineMapProps`;
   *  the card menu's Jobs-at-once write (#6740) rides them too. */
  readonly allBoxes: readonly UtilBox[];
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
};

/** The row's first cell: the dot, the name (a button for an admin's own machine), the sub line. */
function MachineNameCell(props: {
  readonly box: UtilBox;
  readonly onManage: MachineHandlers['onManage'];
}): JSX.Element {
  return (
    <span class="machine-row">
      <Show
        when={props.box.verdict.status === 'working'}
        fallback={
          <span
            class="machine-card__dot"
            data-status={props.box.verdict.status}
            aria-hidden="true"
          />
        }
      >
        <span class="status__dot" aria-hidden="true" />
      </Show>
      <span class="machine-row__stack">
        <button type="button" class="machine-row__name" onClick={() => props.onManage(props.box)}>
          {props.box.display_name}
        </button>
        <span class="machine-row__sub">
          <span>{shortId(props.box.id)}</span>
          <Show when={serverText(props.box)}>{(server) => <span>{server()}</span>}</Show>
        </span>
        <Show when={props.box.via_org}>
          {(org) => (
            <span class="machine-row__from">{t('fleet.machines.from_org', { org: org() })}</span>
          )}
        </Show>
      </span>
    </span>
  );
}

/** Last seen: how long ago, the full moment on hover; "-" before any check-in we can read. */
function SeenCell(props: { readonly iso: string | null }): JSX.Element {
  return (
    <Show when={props.iso} fallback={t('fleet.machines.not_reported')}>
      {(iso) => (
        <time class="machine-row__seen" datetime={iso()} title={absolute(iso())}>
          {ago(iso())}
        </time>
      )}
    </Show>
  );
}

/**
 * Keeps keyboard focus on a sort header across the press. The groups are ordered by the
 * sorted rows, so a press can replace or move the section holding the pressed header, and a
 * browser then drops focus to <body> (`e2e/fleet-list-focus.spec.ts`). When that happens,
 * the pressed button takes focus back if it is still on the page (its section moved), else
 * the same column's header in the first table (its section left the page). Focus the press
 * left in place, or that moved elsewhere on purpose, is never touched.
 */
function createSortFocus(
  root: () => HTMLElement | undefined,
  view: () => FleetViewState,
): (column: ListColumn) => void {
  let pending: { readonly column: ListColumn; readonly button: HTMLElement } | null = null;
  // Keyed on the order itself: a filter, search or group change builds a new view object
  // but is not a sort press.
  const order = createMemo(() => `${view().sort}:${view().dir}`);
  createEffect(
    on(
      order,
      () => {
        const press = pending;
        pending = null;
        const active = document.activeElement;
        if (press === null || (active !== null && active !== document.body && active.isConnected))
          return;
        const target = press.button.isConnected
          ? press.button
          : root()?.querySelector<HTMLElement>(`button[data-sort-key="${press.column}"]`);
        target?.focus();
      },
      { defer: true },
    ),
  );
  return (column) => {
    const button = document.activeElement?.closest<HTMLElement>('.data-table__sort') ?? null;
    pending = button === null ? null : { column, button };
  };
}

export function MachineTable(props: MachineTableProps): JSX.Element {
  let root: HTMLDivElement | undefined;
  const pressed = createSortFocus(
    () => root,
    () => props.view,
  );
  // Every cell reads its machine through the current read, so a poll re-renders in place.
  const cell =
    (render: (box: () => UtilBox) => JSX.Element) =>
    (id: string): JSX.Element => <Show when={props.byId.get(id)}>{(box) => render(box)}</Show>;
  const bar = (kind: ResourceKind) => cell((box) => <MachineResourceBar box={box()} kind={kind} />);
  const RENDER: Readonly<Record<ListColumn, (id: string) => JSX.Element>> = {
    name: cell((box) => <MachineNameCell box={box()} onManage={props.onManage} />),
    status: cell((box) => (
      <span class="machine-row__status">{t(MACHINE_STATUS[box().verdict.status])}</span>
    )),
    // One pick names the row and its repo: the oldest running job (`MachineTaskCell`).
    task: cell((box) => <MachineTaskCell box={box()} />),
    cpu: bar('cpu'),
    memory: bar('memory'),
    disk: bar('disk'),
    health: cell((box) => {
      const health = () => MACHINE_HEALTH[box().verdict.health];
      return <span class={`fleet-verdict fleet-verdict--${health().tone}`}>{t(health().key)}</span>;
    }),
    seen: cell((box) => <SeenCell iso={box().last_beat_iso} />),
  };
  // Built once per mount, never per sort: the builder reads no signal (`t` is fixed at load,
  // and `props.view` is read only inside each header's `state` getter), so a press repaints
  // `aria-sort` in place and the pressed button keeps keyboard focus. A rebuilt column would
  // be a new `<For>` item, and the header cell holding focus would leave the document.
  const columns = createMemo((): DataTableColumn<string>[] => [
    ...LIST_COLUMNS.map(
      (column): DataTableColumn<string> => ({
        key: column,
        header: t(HEADER[column]),
        rowHeader: column === 'name',
        mono: column === 'seen',
        class: `machine-table__${column}`,
        cell: RENDER[column],
        sort: {
          state: () => ariaSort(props.view, column),
          onSort: () => {
            pressed(column);
            props.onSort(column);
          },
        },
      }),
    ),
    {
      key: 'actions',
      header: t('fleet.machines.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      class: 'machine-table__actions',
      cell: cell((box) => (
        <MissionCardMenu
          box={box()}
          canWrite={props.canWrite}
          size={null}
          notify={props.notify}
          onDrain={props.onDrain}
          onUndrain={props.onUndrain}
          onManage={props.onManage}
          onRename={props.onRename}
          onRemove={props.onRemove}
        />
      )),
    },
  ]);
  return (
    <div class="machine-table-view" ref={root}>
      <MachineGroups
        paged={props.paged}
        allBoxes={props.allBoxes}
        token={props.token}
        notify={props.notify}
        onWritten={props.onWritten}
        canWrite={props.canWrite}
        body={(ids, label) => (
          <DataTable
            label={label()}
            class="machine-table"
            columns={columns()}
            rows={ids()}
            rowKey={(id) => id}
          />
        )}
      />
    </div>
  );
}
